import React, { useEffect } from 'react';
import {
    FormControl, Button, Grid, Paper, Typography, OutlinedInput, Box,
    FormLabel, FormHelperText, FormControlLabel, Checkbox, MenuItem, Select
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    createTemplateRequest,
    updateTemplateRequest,
    resetCreateTemplate,
    resetUpdateTemplate
} from '../../../../redux/modules/reportTemplate/slice';

const validationSchema = yup.object().shape({
    name: yup.string().required("Template name is required"),
    category: yup.string().required("Category is required"),
    description: yup.string(),
    clinicalNotes: yup.string(),
    findings: yup.string(),
    impression: yup.string(),
    isNormal: yup.boolean(),
    sortOrder: yup.number().default(0),
});

const categories = [
    { value: 'OB', label: 'OB (Obstetric)' },
    { value: 'GYN', label: 'GYN (Gynecologic)' },
    { value: 'Abdominal', label: 'Abdominal' },
    { value: 'Thyroid', label: 'Thyroid' },
    { value: 'Renal', label: 'Renal' },
    { value: 'Vascular', label: 'Vascular' },
    { value: 'Breast', label: 'Breast' },
    { value: 'Cardiac', label: 'Cardiac' },
    { value: 'Other', label: 'Other' },
];

const Form = ({ itemData, toggleList }) => {
    const dispatch = useDispatch();
    const { createLoading, updateLoading, createRes, updateRes } = useSelector(state => state.reportTemplate);

    const { register, handleSubmit, formState: { errors }, control } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: itemData?.name || '',
            category: itemData?.category || 'OB',
            description: itemData?.description || '',
            clinicalNotes: itemData?.clinicalNotes || '',
            findings: itemData?.findings || '',
            impression: itemData?.impression || '',
            isNormal: itemData?.isNormal || false,
            sortOrder: itemData?.sortOrder || 0,
        }
    });

    useEffect(() => {
        if (createRes) {
            if (createRes?.success) {
                toggleList();
                toast.success(createRes?.message);
            } else {
                toast.error(createRes?.message);
            }
            dispatch(resetCreateTemplate());
        }
        if (updateRes) {
            if (updateRes?.success) {
                toggleList();
                toast.success(updateRes?.message);
            } else {
                toast.error(updateRes?.message);
            }
            dispatch(resetUpdateTemplate());
        }
    }, [createRes, updateRes, toggleList, dispatch]);

    const onSubmit = (data) => {
        if (itemData?._id) {
            dispatch(updateTemplateRequest({ id: itemData._id, ...data }));
        } else {
            dispatch(createTemplateRequest(data));
        }
    };

    const loading = createLoading || updateLoading;

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ padding: 3, mt: 3 }} className='cardStyle'>
                <Typography variant="h5" className='title' sx={{ mb: 3 }}>
                    {itemData?._id ? 'Edit Template' : 'Add New Template'}
                </Typography>

                <Grid container spacing={3}>
                    {/* Basic Info */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <FormLabel>Template Name *</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('name')}
                                size='small'
                                placeholder="e.g., Normal Second Trimester OB"
                            />
                            {errors?.name?.message && <FormHelperText error>{errors?.name?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <FormLabel>Category *</FormLabel>
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} size="small">
                                        {categories.map(cat => (
                                            <MenuItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.category?.message && <FormHelperText error>{errors?.category?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                            <FormLabel>Description</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('description')}
                                size='small'
                                placeholder="Brief description of when to use this template"
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <FormLabel>Sort Order</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('sortOrder')}
                                size='small'
                                type="number"
                                placeholder="0"
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Controller
                            name="isNormal"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Checkbox {...field} checked={field.value} />}
                                    label="This is a 'Normal' template (will be highlighted in the selector)"
                                />
                            )}
                        />
                    </Grid>

                    {/* Template Content */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                            Template Content
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter the text that will be auto-filled when this template is selected
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>Clinical Notes</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('clinicalNotes')}
                                multiline
                                rows={2}
                                placeholder="Pre-filled clinical notes or history..."
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>Findings</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('findings')}
                                multiline
                                rows={8}
                                placeholder="Template findings text..."
                            />
                            <FormHelperText>Use structured text with headings like "FETUS:", "PLACENTA:", etc.</FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>Impression</FormLabel>
                            <OutlinedInput
                                className="inputfield"
                                {...register('impression')}
                                multiline
                                rows={4}
                                placeholder="Template impression/conclusion..."
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{
                display: "flex",
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mt: 3,
                justifyContent: { xs: 'center', sm: 'right' }
            }}>
                <Button
                    className="commonBtn"
                    type='submit'
                    variant='contained'
                    disabled={loading}
                    sx={{ order: { xs: 1, sm: 0 } }}
                >
                    {loading ? 'Saving...' : (itemData?._id ? "Update" : "Save")}
                </Button>
                <Button
                    variant='outlined'
                    className='btn cancelBtn'
                    onClick={() => { toggleList(); }}
                    sx={{ order: { xs: 2, sm: 0 } }}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default Form;
